import { useEffect, useMemo, useRef, useState } from 'react';
import Translation from './exercises/Translation.jsx';
import MultipleChoice from './exercises/MultipleChoice.jsx';
import WordBank from './exercises/WordBank.jsx';
import Listen from './exercises/Listen.jsx';
import Match from './exercises/Match.jsx';
import Cloze from './exercises/Cloze.jsx';
import ImageChoice from './exercises/ImageChoice.jsx';
import Speak from './exercises/Speak.jsx';
import { buildStoryExercises } from '../utils/storyRunner.js';
import { Icons } from './Icons.jsx';
import { ChunkyButton, GhostButton } from './Buttons.jsx';
import { Maja, MajaFloat } from './Mascot.jsx';
import { speak } from '../utils/speech.js';
import { normalize } from '../utils/normalize.js';
import { correctTap, wrongTap, tapLight } from '../utils/haptics.js';
import { generateExercises } from '../utils/exercises.js';
import {
  useProgress,
  recordAnswer,
  completeLesson,
  completeStory,
} from '../store/progress.js';
import { BALL } from '../theme.js';
import { t } from '../i18n.js';

const T = BALL;
const XP_PER_LESSON = 10;
const XP_BONUS_PERFECT = 5;

const MAJA_LESSON_TIPS = [
  { mood: 'wink',  text: 'Psst — read each option before you tap.' },
  { mood: 'happy', text: "If you slow down, you'll catch more of these. 💪" },
  { mood: 'cheer', text: "You're doing great! Keep that streak alive 🔥" },
  { mood: 'happy', text: "Don't worry about typos — close enough counts." },
];

function Body({ exercise, lang, onResult, locked, registerSubmit }) {
  switch (exercise.type) {
    case 'translate':
      return <Translation exercise={exercise} lang={lang} onResult={onResult} locked={locked} registerSubmit={registerSubmit} />;
    case 'choice':
      return <MultipleChoice exercise={exercise} lang={lang} onResult={onResult} locked={locked} />;
    case 'wordbank':
      return <WordBank exercise={exercise} lang={lang} onResult={onResult} locked={locked} registerSubmit={registerSubmit} />;
    case 'listen':
      return <Listen exercise={exercise} lang={lang} onResult={onResult} locked={locked} registerSubmit={registerSubmit} />;
    case 'match':
      return <Match exercise={exercise} lang={lang} onResult={onResult} locked={locked} />;
    case 'cloze':
      return <Cloze exercise={exercise} lang={lang} onResult={onResult} locked={locked} />;
    case 'image-choice':
      return <ImageChoice exercise={exercise} lang={lang} onResult={onResult} locked={locked} />;
    case 'story-line':
      return <StoryLine exercise={exercise} lang={lang} onResult={onResult} locked={locked} />;
    case 'speak':
      return <Speak exercise={exercise} lang={lang} onResult={onResult} locked={locked} />;
    default:
      return null;
  }
}

function StoryLine({ exercise, lang, onResult, locked }) {
  const { character, hr, translation, speakText } = exercise;
  // Auto-play the line once on mount so the user hears native pronunciation
  // before reading. Keeps the story feeling like a dialogue, not a script.
  useEffect(() => {
    if (!speakText) return;
    const tid = setTimeout(() => speak(speakText), 250);
    return () => clearTimeout(tid);
  }, [speakText]);

  const isMaja = character?.id === 'maja';
  const bubbleAlign = isMaja ? 'flex-end' : 'flex-start';
  const bubbleColor = isMaja ? T.primary : T.bgAlt;
  const bubbleInk = isMaja ? '#fff' : T.ink;
  const subInk = isMaja ? 'rgba(255,255,255,0.85)' : T.inkSoft;
  const shadow = isMaja ? T.primaryDeep : T.hairline;

  return (
    <div style={{
      padding: '8px 22px 0',
      display: 'flex', flexDirection: 'column', gap: 16,
      minHeight: 280,
    }}>
      <div style={{
        display: 'flex', justifyContent: bubbleAlign, alignItems: 'flex-end', gap: 10,
      }}>
        {!isMaja && (
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: T.bgAlt, border: `2px solid ${T.hairline}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, flexShrink: 0,
          }}>{character?.emoji}</div>
        )}
        <button
          type="button"
          onClick={() => speak(speakText)}
          style={{
            appearance: 'none', cursor: 'pointer',
            background: bubbleColor, color: bubbleInk,
            border: `2px solid ${shadow}`,
            boxShadow: `0 4px 0 ${shadow}`,
            borderRadius: 18, padding: '12px 16px',
            textAlign: 'left', maxWidth: '78%', fontFamily: 'inherit',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 800 }}>{hr}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: subInk }}>{translation}</div>
        </button>
        {isMaja && (
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: T.bgAlt, border: `2px solid ${T.hairline}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, flexShrink: 0,
          }}>{character?.emoji}</div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
        <ChunkyButton
          color="primary"
          size="md"
          onClick={() => { if (!locked) onResult({ correct: true, given: '', expected: '' }); }}
        >
          {lang === 'en' ? 'Continue' : 'Fortsätt'}
        </ChunkyButton>
      </div>
    </div>
  );
}

function LessonHeader({ progress, onExit }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: 'calc(env(safe-area-inset-top) + 12px) 16px 12px',
    }}>
      <button
        type="button"
        onClick={onExit}
        style={{ appearance: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        {Icons.close(T.inkSoft)}
      </button>
      <div style={{
        flex: 1, height: 14, background: T.hairline, borderRadius: 99, overflow: 'hidden',
      }}>
        <div style={{
          width: `${Math.min(100, progress * 100)}%`, height: '100%',
          background: T.mint, borderRadius: 99,
          boxShadow: 'inset 0 -3px 0 rgba(0,0,0,0.08), inset 0 3px 0 rgba(255,255,255,0.45)',
          transition: 'width 0.3s cubic-bezier(0.2, 0.7, 0.2, 1)',
        }} />
      </div>
    </div>
  );
}

// Detects the common "wrong direction" mistake: the user typed the Croatian
// word when the translation was expected, or vice-versa. We use this to
// nudge them rather than silently accept the wrong-side answer.
function detectDirectionMistake(given, item, lang) {
  if (!given || !item) return null;
  const g = normalize(given);
  if (!g) return null;
  if (g === normalize(item.hr || '')) return 'wroteCroatian';
  if (g === normalize(item[lang] || '')) return 'wroteMeaning';
  return null;
}

function Feedback({ feedback, item, speakText, lang, onContinue, onRetry }) {
  const ok = feedback.correct;
  const bg = ok ? '#E8F8D8' : '#FFE6E6';

  // The Croatian word/phrase tied to this exercise — always show it so the
  // user sees and hears the right pronunciation, regardless of right/wrong.
  const hr = item?.hr || speakText;
  const meaning = item?.[lang];

  const directionMistake = !ok ? detectDirectionMistake(feedback.given, item, lang) : null;

  // Auto-play the Croatian when feedback appears, so the user hears the
  // correct pronunciation without needing to tap.
  useEffect(() => {
    if (!speakText) return;
    const tid = setTimeout(() => speak(speakText), 250);
    return () => clearTimeout(tid);
  }, [speakText]);

  return (
    <div style={{
      background: bg, borderTop: `2px solid ${ok ? '#C2E589' : '#FFB3B3'}`,
      padding: '14px 22px calc(env(safe-area-inset-bottom) + 16px)',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      {/* Top row: status text + Croatian + meaning + speaker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 18, fontWeight: 900, color: ok ? T.mintDeep : T.redDeep,
          }}>
            {ok
              ? (lang === 'en' ? '✓ Correct!' : '✓ Rätt!')
              : (lang === 'en' ? '✗ Not quite' : '✗ Inte riktigt')}
          </div>
          {hr && (
            <div style={{
              fontSize: 15, marginTop: 4, lineHeight: 1.25,
              display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
            }}>
              <span style={{ color: T.primary, fontWeight: 900 }}>{hr}</span>
              {meaning && meaning !== hr && (
                <span style={{ color: T.inkSoft, fontWeight: 600 }}>· {meaning}</span>
              )}
            </div>
          )}
          {directionMistake === 'wroteMeaning' && (
            <div style={{
              fontSize: 12, color: T.primary, marginTop: 6, fontWeight: 800,
            }}>
              💡 {lang === 'en'
                ? "You typed the meaning — type the Croatian word you heard."
                : 'Du skrev översättningen — skriv det kroatiska ordet du hörde.'}
            </div>
          )}
          {directionMistake === 'wroteCroatian' && (
            <div style={{
              fontSize: 12, color: T.primary, marginTop: 6, fontWeight: 800,
            }}>
              💡 {lang === 'en'
                ? 'You typed the Croatian word — type the translation instead.'
                : 'Du skrev det kroatiska ordet — skriv översättningen istället.'}
            </div>
          )}
          {!ok && !directionMistake && feedback.given && feedback.given.trim() && (
            <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 4 }}>
              {lang === 'en' ? 'Your answer' : 'Ditt svar'}: {feedback.given}
            </div>
          )}
        </div>
        {speakText && (
          <button
            type="button"
            onClick={() => speak(speakText)}
            aria-label={lang === 'en' ? 'Replay audio' : 'Spela upp igen'}
            style={{
              appearance: 'none', border: 'none', cursor: 'pointer',
              width: 52, height: 52, borderRadius: '50%',
              background: T.blue, color: '#fff', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 0 ${T.blueDeep}`,
              transition: 'transform 80ms, box-shadow 80ms',
            }}
            onPointerDown={(e) => {
              e.currentTarget.style.transform = 'translateY(3px)';
              e.currentTarget.style.boxShadow = `0 1px 0 ${T.blueDeep}`;
            }}
            onPointerUp={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = `0 4px 0 ${T.blueDeep}`;
            }}
            onPointerLeave={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = `0 4px 0 ${T.blueDeep}`;
            }}
          >
            {Icons.speaker('#fff')}
          </button>
        )}
      </div>

      {/* Bottom row: action buttons. Wrong-state gets a prominent Try Again. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        {!ok && onRetry && (
          <ChunkyButton color="primary" size="md" onClick={onRetry} style={{ flex: 1 }}>
            {lang === 'en' ? 'Try again' : 'Försök igen'}
          </ChunkyButton>
        )}
        <ChunkyButton
          color={ok ? 'mint' : 'red'}
          size="md"
          onClick={onContinue}
          style={!ok ? {} : { flex: 1 }}
        >
          {ok
            ? (lang === 'en' ? 'Continue' : 'Fortsätt')
            : (lang === 'en' ? 'Skip' : 'Hoppa')}
        </ChunkyButton>
      </div>
    </div>
  );
}

export default function Lesson({ skill, lesson, story, onExit, onComplete }) {
  const progress = useProgress();
  const lang = progress.sourceLang;
  const isStory = !!story;

  const initialExercises = useMemo(
    () => isStory
      ? buildStoryExercises(story, lang)
      : generateExercises(lesson.items, {
          sourceLang: lang,
          isReview: lesson.isReview,
          speakEnabled: !!(progress.speakingEnabled && !progress.micDenied),
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isStory ? story : lesson, lang]
  );

  const [queue, setQueue] = useState(initialExercises);
  const [index, setIndex] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [stats, setStats] = useState({ correct: 0, wrong: 0 });
  const [finished, setFinished] = useState(false);
  const [showExit, setShowExit] = useState(false);
  // Bumps when the user taps "Try again" — used as part of the Body key
  // to force the exercise component to remount with cleared input state.
  const [attemptToken, setAttemptToken] = useState(0);

  const current = queue[index];
  const totalForBar = queue.length;
  const completedForBar = index + (feedback ? 1 : 0);

  // Exercises register a submit callback so the footer's "Check" button
  // can drive them without DOM queries.
  const submitRef = useRef(null);
  const registerSubmit = (fn) => { submitRef.current = fn; };

  // Rotating Maja tip per lesson — fixed once per mount so it doesn't flicker.
  const majaTip = useMemo(
    () => MAJA_LESSON_TIPS[Math.floor(Math.random() * MAJA_LESSON_TIPS.length)],
    [lesson.id]
  );

  function handleResult({ correct, given, expected, skipped }) {
    if (feedback) return;
    if (skipped) { advance(); return; }
    // Story-line presentation steps auto-pass without showing the feedback
    // bar — they're just "Continue" panels, not graded exercises.
    if (current.type === 'story-line') {
      setStats((s) => ({ ...s, correct: s.correct + 1 }));
      advance();
      return;
    }
    setFeedback({ correct, given, expected });
    recordAnswer({
      skillId: isStory ? story.id : skill.id,
      hr: current.item?.hr || current.items?.[0]?.hr,
      correct,
    });
    setStats((s) => ({
      correct: s.correct + (correct ? 1 : 0),
      wrong: s.wrong + (correct ? 0 : 1),
    }));
    if (correct) {
      correctTap();
    } else {
      wrongTap();
      // Re-queue wrong exercises so the user sees them again before the
      // lesson ends — that's how the items actually stick. No "lives"
      // attached: failing just means more practice, not a dead-end.
      setQueue((q) => [...q, { ...current, _retry: true }]);
    }
    if (current.type === 'match') {
      setTimeout(advance, 600);
    }
  }

  function tryAgain() {
    tapLight();
    // The wrong answer also got auto-re-queued for end-of-lesson practice;
    // since the user is retrying now, drop that duplicate so they don't see
    // it twice in this lesson.
    setQueue((q) => {
      const next = [...q];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i]._retry && next[i] === current) continue;
        if (next[i]._retry && next[i].item === current.item && next[i].type === current.type) {
          next.splice(i, 1);
          break;
        }
      }
      return next;
    });
    setFeedback(null);
    setAttemptToken((n) => n + 1);
  }

  function advance() {
    tapLight();
    setFeedback(null);
    if (index + 1 >= queue.length) {
      setFinished(true);
      if (isStory) {
        completeStory(story.id, {
          xp: story.rewardXP || 25,
          gems: story.rewardGems || 2,
        });
      } else {
        const perfect = stats.wrong === 0;
        const xp = XP_PER_LESSON + (perfect ? XP_BONUS_PERFECT : 0);
        completeLesson({
          skillId: skill.id,
          lessonId: lesson.id,
          lessonIndex: lesson.index,
          xpEarned: xp,
          gemsEarned: 3 + (perfect ? 2 : 0),
        });
      }
    } else {
      setIndex((i) => i + 1);
    }
  }

  // ─── Lesson complete ────────────────────────────────────────────
  if (finished) {
    const perfect = stats.wrong === 0;
    const xp = XP_PER_LESSON + (perfect ? XP_BONUS_PERFECT : 0);
    const accuracy = Math.round((stats.correct / Math.max(1, stats.correct + stats.wrong)) * 100);
    return (
      <div className="app-shell" style={{ padding: 24 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <Maja size={140} mood="cheer" tshirt={perfect ? 'SUPER!' : 'BRAVO!'} tshirtColor={T.gold} />
          <h1 style={{ fontSize: 28, fontWeight: 900, color: T.ink, marginTop: 12 }}>
            {lang === 'en' ? 'Lesson complete!' : 'Lektion klar!'}
          </h1>
          <div style={{
            display: 'flex', gap: 10, width: '100%', maxWidth: 360,
            justifyContent: 'center', marginTop: 6,
          }}>
            <StatPill icon={Icons.star()} label="XP" value={`+${xp}`} color={T.gold} />
            <StatPill icon={Icons.gem()} label={lang === 'en' ? 'Gems' : 'Ädelst.'} value={`+${3 + (perfect ? 2 : 0)}`} color={T.blue} />
            <StatPill icon={Icons.flame()} label={lang === 'en' ? 'Acc.' : 'Träffsäk.'} value={`${accuracy}%`} color={T.primary} />
          </div>
        </div>
        <div style={{ padding: '20px 0 calc(env(safe-area-inset-bottom) + 8px)', display: 'flex', justifyContent: 'center' }}>
          <ChunkyButton color="primary" onClick={onComplete}>
            {lang === 'en' ? 'Continue' : 'Fortsätt'}
          </ChunkyButton>
        </div>
      </div>
    );
  }

  const checkButtonState = (() => {
    if (current.type === 'choice') return 'hidden';
    if (current.type === 'match') return 'hidden';
    if (current.type === 'cloze') return 'hidden';
    if (current.type === 'image-choice') return 'hidden';
    if (current.type === 'story-line') return 'hidden';
    if (current.type === 'speak') return 'hidden';
    return 'visible';
  })();

  const isInteractive = current.type === 'translate' || current.type === 'listen' || current.type === 'wordbank';
  // Speak and story-line ship their own action controls — suppress the
  // global Skip/Check bar so we don't show parallel buttons.
  const showActionBar = current.type !== 'speak' && current.type !== 'story-line';

  return (
    <div className="app-shell">
      <LessonHeader
        progress={completedForBar / totalForBar}
        onExit={() => setShowExit(true)}
      />

      <div style={{ flex: 1, overflowY: 'auto', position: 'relative', paddingBottom: 20 }}>
        <Body
          key={`${index}-${attemptToken}`}
          exercise={current}
          lang={lang}
          onResult={handleResult}
          locked={!!feedback}
          registerSubmit={registerSubmit}
        />

        {/* Floating Maja tip on every other exercise, except during feedback */}
        {!feedback && index > 0 && index % 3 === 1 && (
          <MajaFloat theme={T} text={majaTip.text} mood={majaTip.mood} />
        )}
      </div>

      {feedback ? (
        <Feedback
          feedback={feedback}
          item={current.item || current.items?.[0]}
          speakText={current.speakText}
          lang={lang}
          onContinue={advance}
          onRetry={current.type !== 'match' ? tryAgain : undefined}
        />
      ) : showActionBar ? (
        <div style={{
          borderTop: `2px solid ${T.hairline}`, background: T.bgAlt,
          padding: '16px 22px calc(env(safe-area-inset-bottom) + 18px)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <GhostButton size="md" onClick={() => {
            // "Skip" — count as wrong + reveal answer.
            const expected = Array.isArray(current.accept)
              ? current.accept[0]
              : current.correct || current.item?.hr;
            handleResult({ correct: false, given: '', expected });
          }}>
            {lang === 'en' ? 'Skip' : 'Hoppa'}
          </GhostButton>
          {checkButtonState === 'visible' && isInteractive && (
            <ChunkyButton
              color="mint"
              onClick={() => submitRef.current?.()}
            >
              {lang === 'en' ? 'Check' : 'Kontrollera'}
            </ChunkyButton>
          )}
        </div>
      ) : null}

      {showExit && (
        <div onClick={() => setShowExit(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24, zIndex: 50,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: T.bgAlt, borderRadius: 18,
            padding: 22, maxWidth: 360, width: '100%',
            display: 'flex', flexDirection: 'column', gap: 16,
          }}>
            <p style={{ color: T.ink, fontSize: 15, fontWeight: 700 }}>
              {lang === 'en'
                ? "Quit lesson? You'll lose progress on this one."
                : 'Avsluta lektionen? Du tappar framstegen i denna.'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <GhostButton size="md" onClick={() => setShowExit(false)}>
                {lang === 'en' ? 'Stay' : 'Stanna'}
              </GhostButton>
              <ChunkyButton color="red" size="md" onClick={onExit}>
                {lang === 'en' ? 'Quit' : 'Avsluta'}
              </ChunkyButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatPill({ icon, value, label, color }) {
  return (
    <div style={{
      background: T.bgAlt, border: `2px solid ${T.hairline}`,
      borderRadius: 14, padding: '14px 8px',
      boxShadow: `0 3px 0 ${T.hairline}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      minWidth: 100,
    }}>
      <div style={{ color }}>{icon}</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: T.ink }}>{value}</div>
      <div style={{
        fontSize: 11, fontWeight: 700, color: T.inkSoft,
        letterSpacing: '0.04em', textTransform: 'uppercase',
      }}>{label}</div>
    </div>
  );
}
